import React from "react";
import { Info, Sparkles, ShieldCheck, BellRing, Mail, Target } from "lucide-react";
import { useT } from "../lib/i18n";
import { useSeo } from "../lib/useSeo";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";

const CONTACT_EMAIL = "support@woningalert.com";

const CONTENT = {
  nl: {
    title: "Over WoningAlert",
    subtitle: "Slim, eerlijk en veilig een huurwoning vinden in Nederland.",
    seoDesc: "Maak kennis met WoningAlert: onze missie, hoe het platform werkt en hoe je ons bereikt.",
    missionTitle: "Onze missie",
    missionBody: "De Nederlandse huurmarkt is razendsnel en oneerlijk verdeeld. WoningAlert geeft woningzoekers een eerlijke kans met een slimme matchscore, directe alerts en een scam-check die oplichting ontmaskert. We zijn onafhankelijk: geen bemiddelingskosten, geen verborgen belangen.",
    howTitle: "Hoe het werkt",
    steps: [
      { icon: Sparkles, t: "Maak je profiel", d: "Vertel wat je zoekt. Wij berekenen per woning een persoonlijke matchscore." },
      { icon: BellRing, t: "Ontvang alerts", d: "Krijg direct bericht bij nieuwe passende woningen — reageer als eerste." },
      { icon: ShieldCheck, t: "Check op oplichting", d: "Twijfel je over een advertentie? De scam-check waarschuwt je op tijd." },
    ],
    contactTitle: "Contact & support",
    contactBody: "Vragen, feedback of hulp nodig? We horen graag van je. We reageren doorgaans binnen één werkdag.",
    contactBtn: "Mail ons",
  },
  en: {
    title: "About WoningAlert",
    subtitle: "Find a rental home in the Netherlands smartly, fairly and safely.",
    seoDesc: "Meet WoningAlert: our mission, how the platform works and how to reach us.",
    missionTitle: "Our mission",
    missionBody: "The Dutch rental market is fast and unfairly stacked. WoningAlert gives home seekers a fair shot with a smart match score, instant alerts and a scam check that exposes fraud. We're independent: no brokerage fees, no hidden interests.",
    howTitle: "How it works",
    steps: [
      { icon: Sparkles, t: "Create your profile", d: "Tell us what you're looking for. We compute a personal match score per home." },
      { icon: BellRing, t: "Receive alerts", d: "Get notified instantly about new matching homes — respond first." },
      { icon: ShieldCheck, t: "Check for scams", d: "Not sure about a listing? The scam check warns you in time." },
    ],
    contactTitle: "Contact & support",
    contactBody: "Questions, feedback or need help? We'd love to hear from you. We usually reply within one business day.",
    contactBtn: "Email us",
  },
};

const OverOns = () => {
  const { lang } = useT();
  const c = CONTENT[lang] || CONTENT.nl;
  useSeo({ title: c.title, description: c.seoDesc, image: "/og-image.png", path: "/over-ons" });

  return (
    <div>
      <PageHeader icon={Info} title={c.title} subtitle={c.subtitle} />
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <div className="rounded-3xl border border-border bg-brandblue-light/40 p-8">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brandblue text-white"><Target size={20} /></span>
            <h2 className="font-display text-xl font-bold text-navy">{c.missionTitle}</h2>
          </div>
          <p className="mt-4 leading-relaxed text-slate-600">{c.missionBody}</p>
        </div>

        <h2 className="mt-12 font-display text-2xl font-extrabold text-navy">{c.howTitle}</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {c.steps.map((s, i) => (
            <div key={i} className="rounded-3xl border border-border bg-white p-6 shadow-sm">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brandblue-light text-brandblue"><s.icon size={22} /></div>
              <h3 className="mt-4 font-display text-base font-bold text-navy">{s.t}</h3>
              <p className="mt-1.5 text-sm text-slate-500">{s.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 overflow-hidden rounded-3xl bg-gradient-to-br from-brandblue to-navy p-8 text-center text-white">
          <Mail className="mx-auto" size={28} />
          <h2 className="mt-4 font-display text-2xl font-extrabold">{c.contactTitle}</h2>
          <p className="mx-auto mt-2 max-w-md text-white/70">{c.contactBody}</p>
          <Button asChild className="mt-6 rounded-full bg-white px-8 text-navy hover:bg-white/90">
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email">{c.contactBtn} · {CONTACT_EMAIL}</a>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OverOns;
